"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var Localize_1 = require("../../../gen-src/Localize");
var SharedWidgets_1 = require("../../SharedWidgets");
var Standard_1 = require("../../styles/Standard");
var TabStyles_1 = require("../../styles/TabStyles");
var BrowserDetection_1 = require("../../utils/BrowserDetection");
var DomUtil_1 = require("../../utils/DomUtil");
var Overlay_1 = require("../../utils/Overlay");
var List_1 = require("../List/List");
var ListItemUtils_1 = require("../List/ListItemUtils");
var TextFieldInputWidget_1 = require("../TextField/TextFieldInputWidget");
var TextFieldStyleName = TypeStyle.style({
    backgroundColor: 'transparent',
    border: 'none',
    color: '#333',
    fontSize: TabStyles_1.TabStyles.Typography.Sizing.Default,
    height: TabStyles_1.TabStyles.Sizing.RowHeight,
    lineHeight: TabStyles_1.TabStyles.Sizing.RowHeight + "px",
    padding: 0,
});
var PlaceholderStyleName = TypeStyle.style({
    color: '#707070',
    lineHeight: TabStyles_1.TabStyles.Sizing.RowHeight + "px",
    pointerEvents: 'none',
    position: 'absolute',
});
var DefaultState = Object.freeze({ inputText: '', isOpen: false, selectedItemIndex: -1 });
var OverlayPosition = Object.freeze({ horizontal: Overlay_1.HorizontalOriginOption.left, vertical: Overlay_1.VerticalOriginOption.bottom });
/**
 * Internal implementation of an ARIA-spec combobox for use with the ChipPicker widget.
 * Not exported separately in SharedWidgets.ts as it is not Tableau-styled and has some ChipPicker-specific concerns baked in.
 */
var Combobox = /** @class */ (function (_super) {
    tslib_1.__extends(Combobox, _super);
    function Combobox() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.inputElement = null;
        _this.listId = DomUtil_1.generateId();
        _this.measureRef = React.createRef();
        _this.state = tslib_1.__assign({}, DefaultState);
        _this.handleSelectValue = function (value) {
            _this.setState(DefaultState, function () { return _this.props.onSelectValue(value); });
        };
        _this.handleInputChange = function (inputText, isComposing) {
            _this.setState({ inputText: inputText, isOpen: _this.state.isOpen || !!inputText });
        };
        /** https://www.w3.org/TR/wai-aria-practices-1.1/#combobox */
        _this.inputKeyDownHandlers = Object.freeze({
            'ArrowUp': function (e) {
                if (_this.state.isOpen) {
                    if (e.altKey) {
                        _this.inputKeyDownHandlers.Escape(e);
                    }
                    else {
                        _this.setState({ selectedItemIndex: ListItemUtils_1.findSelectableItemIndex(_this.props.listItems, _this.state.selectedItemIndex, -1) });
                    }
                }
            },
            'ArrowDown': function (e) {
                if (!_this.state.isOpen) {
                    _this.setState({ isOpen: true });
                }
                if (!e.altKey) {
                    _this.setState({ selectedItemIndex: ListItemUtils_1.findSelectableItemIndex(_this.props.listItems, _this.state.selectedItemIndex, +1) });
                }
            },
            'Enter': function () {
                if (_this.state.isOpen && _this.state.selectedItemIndex >= 0) {
                    _this.handleSelectValue(_this.props.listItems[_this.state.selectedItemIndex].value);
                }
            },
            'Escape': function (e) {
                if (_this.state.isOpen) {
                    if (!e.repeat) {
                        _this.inputKeyUpHandlers[e.key] = function () { return _this.setState({ isOpen: false, selectedItemIndex: -1 }, function () { return _this.inputElement.focus(); }); };
                    }
                    return false;
                }
                return true;
            },
            'Ctrl+Home': function () {
                if (_this.state.isOpen) {
                    _this.setState({ selectedItemIndex: ListItemUtils_1.findSelectableItemIndex(_this.props.listItems, -1, 1) });
                }
            },
            'Ctrl+End': function () {
                if (_this.state.isOpen) {
                    _this.setState({ selectedItemIndex: ListItemUtils_1.findSelectableItemIndex(_this.props.listItems, _this.props.listItems.length, -1) });
                }
            }
        });
        _this.handleInputKeyDown = function (e) {
            var key = (BrowserDetection_1.isCtrl(e) ? 'Ctrl+' : '') + e.key;
            if (_this.inputKeyDownHandlers[key]) {
                if (!_this.inputKeyDownHandlers[key](e)) { // handler returning falsy means the event was handled
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
            else if (_this.state.selectedItemIndex >= 0 && e.which >= 32) {
                // resets aria-activedescendant
                _this.setState({ selectedItemIndex: -1 });
            }
        };
        /** Useful for responding to full down-then-up key presses, rather than just down. */
        _this.inputKeyUpHandlers = {};
        _this.handleInputKeyUp = function (e) {
            var key = (BrowserDetection_1.isCtrl(e) ? 'Ctrl+' : '') + e.key;
            if (_this.inputKeyUpHandlers[key]) {
                _this.inputKeyUpHandlers[key](e);
                delete _this.inputKeyUpHandlers[key];
                e.stopPropagation();
                e.preventDefault();
            }
        };
        // TextFieldInputWidget stupidly doesn't expose the event object in its event, so we have to bind this one separately/internally
        _this.handleInputBlur = function (e) {
            var clickingIntoList = _this.state.isOpen && document.getElementById(_this.listId).contains(DomUtil_1.findBlurRelatedTarget(e));
            if (!clickingIntoList) {
                _this.setState(DefaultState);
            }
        };
        _this.handleInputFocus = function (focus) {
            _this.updateInputWidth(focus);
        };
        _this.handleInputRef = function (ref) {
            _this.inputElement = ref;
            if (_this.props.inputRef) {
                _this.props.inputRef(ref);
            }
        };
        _this.handlePortalKey = function (e) {
            // allow Escape events from the popup to close the popup
            if (e.key === 'Escape') {
                e.type === 'keydown' ? _this.handleInputKeyDown(e) : _this.handleInputKeyUp(e);
            }
        };
        _this.getAnchorClientRect = function () {
            if (_this.props.getAnchorClientRect) {
                return _this.props.getAnchorClientRect();
            }
            else {
                return _this.inputElement.getBoundingClientRect();
            }
        };
        // fake a placeholder with a positioned div because the text field is usually too narrow to fit any real placeholder text
        _this.renderPlaceholderText = function (placeholder) {
            return React.createElement("span", { "aria-hidden": true, className: PlaceholderStyleName }, placeholder);
        };
        // hidden div used to measure the input field text, used to resize the field as the user types
        _this.renderMeasurementDiv = function (inputText) {
            return React.createElement("span", { ref: _this.measureRef, style: { whiteSpace: 'pre', position: 'absolute', visibility: 'hidden' } }, inputText);
        };
        return _this;
    }
    Combobox.prototype.componentDidMount = function () {
        this.updateInputWidth();
    };
    Combobox.prototype.componentDidUpdate = function (prevProps, prevState) {
        // reset aria-activedescendant after filtering
        if (prevProps.listItems !== this.props.listItems) {
            var firstItem = this.props.listItems[0];
            this.setState({
                selectedItemIndex: ListItemUtils_1.isSelectableItem(firstItem) && firstItem.value === this.state.inputText ? 0 : -1
            });
        }
        // filter items as the user types
        if (this.state.inputText !== prevState.inputText) {
            if (this.props.onFilterItems) {
                this.props.onFilterItems(this.state.inputText);
            }
            // done here rather than `render` because otherwise the measurement will always be one character behind
            this.updateInputWidth();
        }
        // scroll the selected popup item into view
        if (prevState.selectedItemIndex !== this.state.selectedItemIndex && this.state.selectedItemIndex >= 0) {
            var itemElem = document.getElementById(this.listId).querySelector('[aria-selected="true"]');
            DomUtil_1.scrollIntoView(itemElem);
        }
    };
    // dynamically size the `<input>` to its contents; this is ChipPicker-specific but messy to pull it out
    Combobox.prototype.updateInputWidth = function (isFocused) {
        if (isFocused === void 0) { isFocused = true; }
        var minWidth = isFocused ? (BrowserDetection_1.isEdge ? 4 : 2) : 0;
        this.inputElement.style.width = ((this.measureRef.current || { scrollWidth: 0 }).scrollWidth + minWidth) + 'px';
    };
    Combobox.prototype.render = function () {
        var _a = this.props, className = _a.className, listItems = _a.listItems, id = _a.id, listMaxHeight = _a.listMaxHeight, placeholder = _a.placeholder, stackingRootId = _a.stackingRootId, testId = _a.testId, zIndex = _a.zIndex;
        var _b = this.state, inputText = _b.inputText, isOpen = _b.isOpen, selectedItemIndex = _b.selectedItemIndex;
        var selectedValue = listItems[selectedItemIndex] && listItems[selectedItemIndex].value;
        var activeItemId = (isOpen && selectedItemIndex >= 0) ? this.listId + "-" + selectedItemIndex : undefined;
        var renderItems = listItems.length === 0 ?
            [{ value: Localize_1.Messages.noMatches(), disabled: true }] :
            ListItemUtils_1.defaultItemFilter(listItems, ''); // filter out e.g. consecutive separators
        // follows the ARIA 1.0 Combobox design pattern; the ARIA 1.1 pattern is designed around a "composite" widget which this is not
        // see: https://www.levelaccess.com/differences-aria-1-0-1-1-changes-rolecombobox/
        return React.createElement(React.Fragment, null,
            React.createElement(TextFieldInputWidget_1.TextFieldInputWidget, { "aria-activedescendant": activeItemId, "aria-autocomplete": 'list', "aria-controls": isOpen ? this.listId : undefined, "aria-expanded": isOpen, "aria-haspopup": 'listbox', "aria-placeholder": placeholder, className: TypeStyle.classes(TextFieldStyleName, className), handleBlur: this.handleInputBlur, handleChange: this.handleInputChange, handleFocus: this.handleInputFocus, id: id, inputRef: this.handleInputRef, onKeyDown: this.handleInputKeyDown, onKeyUp: this.handleInputKeyUp, role: 'combobox', testId: testId, text: inputText }),
            inputText ? this.renderMeasurementDiv(inputText) : (placeholder && this.renderPlaceholderText(placeholder)),
            isOpen &&
                React.createElement(Overlay_1.Overlay, { allowAutoPosition: true, anchorOrigin: OverlayPosition, getAnchorClientRect: this.getAnchorClientRect, portalEventTrapOverrides: { onKeyDown: this.handlePortalKey, onKeyUp: this.handlePortalKey }, stackingRootId: stackingRootId, zIndex: zIndex },
                    React.createElement(List_1.List, { disableTabAccessibility: true, id: this.listId, items: renderItems, onSelect: this.handleSelectValue, selectedValue: selectedValue, style: tslib_1.__assign({}, Standard_1.PopupStyle, { maxHeight: listMaxHeight, minWidth: this.getAnchorClientRect().width }), testId: SharedWidgets_1.testId(testId, 'List') })));
    };
    Combobox.defaultProps = { listItems: [] };
    return Combobox;
}(React.PureComponent));
exports.Combobox = Combobox;
//# sourceMappingURL=Combobox.js.map